<template>
  <div class="hds-page">
    <div class="hds-bg">
      <div class="hds-bg-gradient" />
    </div>

    <div class="hds-main" :style="{ paddingTop: statusBarHeight + 'px' }">
      <header class="hds-nav">
        <button type="button" class="hds-nav-back" aria-label="返回" @click="goBack">
          <i class="el-icon-arrow-left" />
        </button>
        <h1 class="hds-nav-title">健康日报</h1>
        <button type="button" class="hds-history-btn" @click="goHistoryList">
          历史报告
        </button>
      </header>

      <div v-if="loading" class="hds-loading">报告加载中...</div>
      <div v-else-if="loadError" class="hds-loading">
        <p>{{ loadError }}</p>
        <button type="button" class="hds-retry" @click="fetchReport">重试</button>
      </div>

      <template v-else>
      <!-- 顶部总览（常驻） -->
      <header class="hds-hero">
        <div v-if="reportDate" class="hds-content-date">{{ reportDate }}</div>
        <div v-if="dayStatus.label || headerSummary.length" class="hds-day-status">
          <div v-if="dayStatus.label" class="hds-day-status-head">
            <div class="hds-day-status-head-label">健康状态</div>
            <div class="hds-day-status-score-row">
              <div v-if="healthScore != null" class="hds-day-status-score">
                <span class="hds-day-status-num">{{ healthScore }}</span>
                <span class="hds-day-status-unit">分</span>
              </div>
              <div
                v-if="dayStatus.label"
                class="hds-day-status-badge"
                :class="statusBadgeClass"
              >
                {{ dayStatus.label }}
              </div>
            </div>
          </div>

          <ul v-if="headerSummary.length" class="hds-day-status-summaries">
            <li
              v-for="(item, index) in headerSummary"
              :key="'hds-summary-' + index"
              class="hds-day-status-item"
              :class="{ 'is-warning': isHeaderSummaryWarning(item) }"
            >
              <span class="hds-day-status-dot" />
              <p class="hds-day-status-text">{{ item }}</p>
            </li>
          </ul>
        </div>
      </header>

      <!-- 模块 Tab（可横向滑动，边缘渐隐 + 箭头提示） -->
      <div
        class="hds-module-tabs-wrap"
        :class="{
          'is-scroll-left': tabsCanScrollLeft,
          'is-scroll-right': tabsCanScrollRight
        }"
      >
        <nav
          ref="moduleTabs"
          class="hds-module-tabs"
          aria-label="日报模块"
          @scroll.passive="updateTabsScrollHint"
        >
          <button
            v-for="tab in visibleModuleTabs"
            :key="tab.key"
            type="button"
            class="hds-module-tab"
            :class="{ active: activeModule === tab.key }"
            @click="switchModule(tab.key)"
          >
            {{ tab.label }}
          </button>
        </nav>
        <span
          v-show="tabsCanScrollLeft"
          class="hds-tabs-hint hds-tabs-hint--left"
          aria-hidden="true"
        >
          <svg viewBox="0 0 12 12" width="12" height="12" fill="none">
            <path d="M7.5 2.5L4 6l3.5 3.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </span>
        <span
          v-show="tabsCanScrollRight"
          class="hds-tabs-hint hds-tabs-hint--right"
          aria-hidden="true"
        >
          <svg viewBox="0 0 12 12" width="12" height="12" fill="none">
            <path d="M4.5 2.5L8 6l-3.5 3.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </span>
      </div>

      <div class="hds-module-body">
        <!-- 基础指标 -->
        <section v-if="activeModule === 'overview'" class="hds-card">
          <div class="hds-card-head">
            <h2 class="hds-card-title">基础指标概览</h2>
          </div>
          <ul class="hds-metric-list">
            <li v-for="item in basicMetrics" :key="item.label" class="hds-metric-row">
              <span class="hds-metric-label">{{ item.label }}</span>
              <span class="hds-metric-value" :class="metricValueClass(item)">{{ item.value }}</span>
              <span class="hds-status" :class="metricStatusClass(item)">
                {{ metricStatusIcon(item) }}
              </span>
            </li>
          </ul>
        </section>

        <!-- 健康预警 -->
        <section v-else-if="activeModule === 'warn'" class="hds-card hds-card--warn">
          <div class="hds-card-head hds-card-head--warn">
            <h2 class="hds-card-title">健康预警</h2>
          </div>
          <p v-if="warnEmptyCopy && !warnTypes.length" class="hds-empty-tip">{{ warnEmptyCopy }}</p>
          <template v-else>
            <div ref="warnTimelineChart" class="hds-chart hds-chart--timeline" />
            <ul class="hds-warn-list">
              <li v-for="item in warnTypes" :key="item.warnType || item.name" class="hds-warn-item">
                <span class="hds-warn-dot" :style="{ background: item.color }" />
                <span class="hds-warn-name">{{ item.name }}</span>
                <span class="hds-warn-count">{{ item.count }}次</span>
              </li>
            </ul>
          </template>
        </section>

        <!-- 中医五藏 -->
        <section v-else-if="activeModule === 'tcm'" class="hds-card">
          <div class="hds-card-head">
            <h2 class="hds-card-title">中医五藏态势</h2>
          </div>
          <p v-if="!hasHeartAssessData" class="hds-empty-tip">{{ heartAssessTip }}</p>
          <template v-else>
            <div class="hds-organ-tabs">
              <button
                v-for="org in organTabs"
                :key="org.key"
                type="button"
                class="hds-organ-tab"
                :class="{ active: activeOrgan === org.key }"
                @click="activeOrgan = org.key"
              >
                {{ org.label }}
              </button>
            </div>

            <div class="hds-heart-assess">
              <div class="hds-sub-title">{{ currentOrganLabel }}态势评估</div>
              <template v-if="organDimensions.length">
                <div v-if="currentOrganStatus" class="hds-heart-level">
                  <div class="hds-heart-level-text">{{ currentOrganStatus }}</div>
                </div>

                <div class="hds-dim-tabs">
                  <div
                    v-for="dim in organDimensions"
                    :key="dim.key"
                    class="hds-dim-tab"
                    :class="scoreToneClass(dim.score)"
                  >
                    <span class="hds-dim-tab-label">{{ dim.label }}</span>
                    <span class="hds-dim-tab-score">
                      {{ dim.score }}<span class="hds-dim-tab-unit">分</span>
                    </span>
                  </div>
                </div>

                <div class="hds-dim-info">
                  <div class="hds-dim-row">
                    <span class="hds-dim-label">常见症状</span>
                    <span class="hds-dim-value">{{ currentHeartDim.symptoms }}</span>
                  </div>
                  <div class="hds-dim-row">
                    <span class="hds-dim-label">好发疾病</span>
                    <span class="hds-dim-value">{{ currentHeartDim.diseases }}</span>
                  </div>
                </div>
              </template>
              <p v-else class="hds-empty-tip">暂无数据</p>

              <button type="button" class="hds-tcm-btn" @click="onTcmDetail">
                中医报告详情
              </button>
            </div>
          </template>
        </section>

        <!-- 心率趋势 -->
        <template v-else-if="activeModule === 'hr'">
          <section class="hds-card">
            <!-- <div class="hds-card-head hds-card-head--row">
              <h2 class="hds-card-title">心率趋势</h2>
              <span class="hds-status" :class="hrHasWarn ? 'warn' : 'ok'">
                {{ hrHasWarn ? '!' : '✓' }}
              </span>
            </div> -->
            <div ref="hrChart" class="hds-chart hds-chart--line" />
            <ul class="hds-metric-list">
              <li v-for="item in hrMetrics" :key="item.label" class="hds-metric-row">
                <span class="hds-metric-label">{{ item.label }}</span>
                <span class="hds-metric-value" :class="metricValueClass(item)">{{ item.value }}</span>
                <span class="hds-status" :class="metricStatusClass(item)">
                  {{ metricStatusIcon(item) }}
                </span>
              </li>
            </ul>
            <div v-if="hrIdealText" class="hds-range-pill">
              <span>{{ hrIdealText }}</span>
            </div>
            <div v-if="hrAdvice.length" class="hds-ai-block">
              <div class="hds-sub-title">AI 健康建议</div>
              <ul class="hds-advice">
                <li v-for="(tip, i) in hrAdvice" :key="'hr-a-' + i">{{ tip }}</li>
              </ul>
            </div>
          </section>

          <section v-if="hrWarnTips.length" class="hds-card hds-card--clip">
            <div class="hds-card-head">
              <h2 class="hds-card-title">健康警示提醒</h2>
            </div>
            <p class="hds-subtitle">基于您的性别、年龄、疾病史等个人健康信息：</p>
            <ul class="hds-bullet-list">
              <li v-for="(row, i) in hrWarnTips" :key="'hr-w-' + i" v-html="row" />
            </ul>
          </section>
        </template>

        <!-- 血压趋势（手表血压 + 血压计） -->
        <template v-else-if="activeModule === 'bp'">
          <section class="hds-card">
            <div class="hds-card-head">
              <h2 class="hds-card-title">手表血压监测</h2>
            </div>
            <p v-if="!hasWatchBp" class="hds-empty-tip">{{ watchBpEmptyCopy }}</p>
            <template v-else>
              <div ref="watchBpChart" class="hds-chart hds-chart--line" />
              <div class="hds-legend">
                <span class="hds-legend-item"><i class="dot blue" />收缩压</span>
                <span class="hds-legend-item"><i class="dot green" />舒张压</span>
              </div>
              <ul class="hds-metric-list">
                <li v-for="item in watchBpMetrics" :key="item.label" class="hds-metric-row">
                  <span class="hds-metric-label">{{ item.label }}</span>
                  <span class="hds-metric-value" :class="metricValueClass(item)">{{ item.value }}</span>
                  <span class="hds-status" :class="metricStatusClass(item)">
                    {{ metricStatusIcon(item) }}
                  </span>
                </li>
              </ul>
            </template>
          </section>

          <section class="hds-card">
            <div class="hds-card-head">
              <h2 class="hds-card-title">血压计监测</h2>
            </div>
            <p v-if="!hasMeterBp" class="hds-empty-tip">{{ meterEmptyCopy }}</p>
            <template v-else>
              <div class="hds-bp-records">
                <div
                  v-for="(item, idx) in meterRecords"
                  :key="item.time + '-' + idx"
                  class="hds-bp-record"
                >
                  <div class="hds-bp-record-top">
                    <span class="hds-bp-time">{{ item.time }}</span>
                    <span class="hds-status" :class="metricStatusClass(item)">
                      {{ metricStatusIcon(item) }}
                    </span>
                  </div>
                  <div class="hds-bp-record-body">
                    <span>舒张压 {{ item.diastolic }}</span>
                    <span>收缩压 {{ item.systolic }}</span>
                  </div>
                </div>
              </div>
              <p class="hds-hint">建议每天固定时间测量，统计趋势变化</p>
            </template>
            <div v-if="idealBp.systolic !== '—'" class="hds-ideal-card">
              <div class="hds-ideal-head">
                <span>您的理想血压范围</span>
                <span class="hds-muted">基于健康档案</span>
              </div>
              <div class="hds-ideal-body">
                <span>舒张压 {{ idealBp.diastolic }}</span>
                <span>收缩压 {{ idealBp.systolic }}</span>
              </div>
            </div>
          </section>

          <section v-if="bpAdvice.length" class="hds-card">
            <div class="hds-card-head">
              <h2 class="hds-card-title">AI 健康建议</h2>
            </div>
            <ul class="hds-advice">
              <li v-for="(tip, i) in bpAdvice" :key="'bp-a-' + i">{{ tip }}</li>
            </ul>
          </section>

          <section v-if="bpWarnTips.length" class="hds-card hds-card--clip">
            <div class="hds-card-head">
              <h2 class="hds-card-title">健康警示提醒</h2>
            </div>
            <p class="hds-subtitle">基于您的性别、年龄、疾病史等个人健康信息：</p>
            <ul class="hds-bullet-list">
              <li v-for="(row, i) in bpWarnTips" :key="'bp-w-' + i" v-html="row" />
            </ul>
          </section>
        </template>

        <!-- 睡眠趋势 -->
        <section v-else-if="activeModule === 'sleep'" class="hds-card">
          <div class="hds-card-head">
            <h2 class="hds-card-title">睡眠趋势</h2>
          </div>

          <div v-if="hasSleepData" class="hds-sleep">
            <div class="hds-sleep-panel">
              <div class="hds-sleep-total">{{ sleepData.totalText }}</div>
              <div v-if="sleepData.timeRange" class="hds-sleep-range">{{ sleepData.timeRange }}</div>
              <div ref="sleepScoreChart" class="hds-chart hds-chart--sleep" />
              <div class="hds-sleep-stages">
                <div
                  v-for="item in sleepData.stages"
                  :key="item.label"
                  class="hds-sleep-stage"
                >
                  <span class="hds-sleep-stage-dot" :style="{ background: item.color }" />
                  <div class="hds-sleep-stage-label">{{ item.label }}</div>
                  <div class="hds-sleep-stage-value">{{ item.value }}</div>
                </div>
              </div>
            </div>

            <div v-if="sleepData.warnText" class="hds-sleep-warn-row">
              <span class="hds-sleep-warn-text">{{ sleepData.warnText }}</span>
              <span class="hds-status" :class="sleepData.warnOk ? 'ok' : 'warn'">
                {{ sleepData.warnOk ? '✓' : '!' }}
              </span>
            </div>

            <div v-if="sleepData.suggestion" class="hds-ai-block">
              <div class="hds-sub-title">睡眠建议</div>
              <ul class="hds-advice">
                <li>{{ sleepData.suggestion }}</li>
              </ul>
            </div>

            <div v-if="sleepData.aiAdvice.length" class="hds-ai-block">
              <div class="hds-sub-title">AI 健康建议</div>
              <ul class="hds-advice">
                <li v-for="(tip, i) in sleepData.aiAdvice" :key="'sleep-ai-' + i">{{ tip }}</li>
              </ul>
            </div>
          </div>

          <template v-else>
            <p class="hds-empty-tip">睡眠时间尚未佩戴，暂无数据</p>
            <p v-if="sleepEmptyDesc" class="hds-desc">{{ sleepEmptyDesc }}</p>
          </template>
        </section>
      </div>

      <p v-if="disclaimer" class="hds-disclaimer">{{ disclaimer }}</p>
      </template>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { mapState, mapActions } from 'pinia'
import { useGlobalStore } from '@/stores/global'
import { useHealthReportV2UnreadStore } from '@/stores/healthReportV2Unread'
import {
  getHealthReportDailyV2,
  getHealthReportDailyV2Detail,
  markHealthReportV2AsRead
} from '@/api/activity'
import {
  isReportOk,
  metricRow,
  metricWarn,
  statusColor,
  warnHex,
  parseHour,
  formatHm,
  splitAdvice,
  mapWarnTypes,
  parseIdealBp,
  mapTcm,
  resolveDailyReportDate,
  hasRealMetric,
  hasNumericPoints,
  scoreToneClass
} from '@/utils/healthReportV2'

/**
 * 健康日报展示页（V2 接口联调）
 */
export default {
  name: 'HealthDailyShowcase',
  data() {
    return {
      loading: true,
      loadError: '',
      disclaimer: '',
      reportDate: '',
      healthScore: null,
      hasMonitoringData: false,
      headerSummary: [],
      dayStatus: { label: '', color: '#8a9691' },
      /** 当前模块 Tab */
      activeModule: 'overview',
      /** 模块 Tab 左侧是否还有可滑内容 */
      tabsCanScrollLeft: false,
      /** 模块 Tab 右侧是否还有可滑内容 */
      tabsCanScrollRight: false,
      moduleTabs: [
        { key: 'overview', label: '基础指标' },
        { key: 'warn', label: '健康预警' },
        { key: 'tcm', label: '中医五藏' },
        { key: 'hr', label: '心率趋势' },
        { key: 'bp', label: '血压趋势' },
        { key: 'sleep', label: '睡眠趋势' }
      ],
      basicMetrics: [],
      warnTimeline: [],
      warnTypes: [],
      warnEmptyCopy: '',
      heartAssessTip: '佩戴时间不足，无法评估心脏健康状态',
      hasHeartAssessData: false,
      tcmOrgans: [],
      organTabs: [
        { key: 'heart', label: '心' },
        { key: 'liver', label: '肝' },
        { key: 'spleen', label: '脾' },
        { key: 'kidney', label: '肾' }
      ],
      activeOrgan: 'heart',
      hrTimes: [],
      hrSeries: [],
      hrMaxMetric: { value: null, warn: false },
      hrMinMetric: { value: null, warn: false },
      hrMetrics: [],
      hrIdealText: '',
      hrAdvice: [],
      hrWarnTips: [],
      hasWatchBp: false,
      watchBpEmptyCopy: '今日暂无手表血压数据',
      watchTimes: [],
      watchSys: [],
      watchDia: [],
      watchBpMaxSysMetric: { value: null, warn: false },
      watchBpMaxDiaMetric: { value: null, warn: false },
      watchBpMetrics: [],
      hasMeterBp: false,
      meterEmptyCopy: '',
      meterRecords: [],
      idealBp: { diastolic: '—', systolic: '—' },
      bpAdvice: [],
      bpWarnTips: [],
      sleepEmptyDesc: '',
      hasSleepData: false,
      sleepData: {
        totalText: '',
        timeRange: '',
        score: 0,
        warnText: '',
        warnOk: true,
        suggestion: '',
        stages: [],
        aiAdvice: []
      },
      chartMap: {}
    }
  },
  computed: {
    /**
     * @returns {Object|null}
     */
    currentTcmOrgan() {
      return this.tcmOrgans.find((d) => d.key === this.activeOrgan) || null
    },
    /**
     * @returns {string}
     */
    currentOrganLabel() {
      const org = this.organTabs.find((d) => d.key === this.activeOrgan)
      return (org && org.label) || ''
    },
    /**
     * @returns {string}
     */
    currentOrganStatus() {
      return (this.currentTcmOrgan && this.currentTcmOrgan.statusName) || ''
    },
    /**
     * @returns {Array}
     */
    organDimensions() {
      return (this.currentTcmOrgan && this.currentTcmOrgan.dims) || []
    },
    /**
     * @returns {{ symptoms?: string, diseases?: string }}
     */
    currentHeartDim() {
      return this.organDimensions[0] || { symptoms: '暂无', diseases: '暂无' }
    },
    /**
     * 有数据才展示的模块 Tab
     * @returns {Array<{ key: string, label: string }>}
     */
    visibleModuleTabs() {
      return (this.moduleTabs || []).filter((tab) => this.isModuleTabVisible(tab.key))
    },
    /**
     * @returns {boolean}
     */
    hrHasWarn() {
      return this.hrMetrics.some((m) => m.warn)
    },
    /**
     * 是否有心率趋势数据
     * @returns {boolean}
     */
    hasHrData() {
      return hasNumericPoints(this.hrSeries) || (this.hrMetrics || []).some((m) => {
        const v = m && m.value
        return v != null && v !== '' && v !== '—'
      })
    },
    /**
     * 今日健康状态徽章样式
     * @returns {string}
     */
    statusBadgeClass() {
      const label = this.dayStatus.label || ''
      if (label === '优秀') return 'is-excellent'
      if (label === '良好') return 'is-good'
      if (label === '一般') return 'is-fair'
      return 'is-default'
    },
    ...mapState(useGlobalStore, ['statusBarHeight'])
  },
  watch: {
    '$route.query.reportDate'() {
      this.fetchReport()
    },
    '$route.query.reportId'() {
      this.fetchReport()
    },
    '$route.query.tab'(val) {
      this.syncActiveModuleFromQuery(val)
      if (this.loading) return
      this.disposeCharts()
      this.$nextTick(() => {
        this.initChartsForModule(this.activeModule)
      })
    },
    visibleModuleTabs() {
      this.ensureActiveModuleVisible()
      this.$nextTick(() => {
        this.updateTabsScrollHint()
      })
    }
  },
  mounted() {
    document.title = '健康日报'
    this.syncActiveModuleFromQuery()
    this.fetchReport()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    this.disposeCharts()
  },
  methods: {
    ...mapActions(useHealthReportV2UnreadStore, {
      applyLocalDailyRead: 'applyLocalDailyRead',
      fetchReportUnread: 'fetchUnread'
    }),
    scoreToneClass,
    /**
     * 指标数值样式：异常红、暂无灰
     * @param {{ warn?: boolean, empty?: boolean }} item
     * @returns {Object}
     */
    metricValueClass(item) {
      return {
        danger: !!(item && item.warn),
        muted: !!(item && item.empty)
      }
    },
    /**
     * 指标状态圆标样式
     * @param {{ warn?: boolean, empty?: boolean }} item
     * @returns {string}
     */
    metricStatusClass(item) {
      if (item && item.empty) return 'empty'
      if (item && item.warn) return 'warn'
      return 'ok'
    },
    /**
     * 指标状态圆标图标
     * @param {{ warn?: boolean, empty?: boolean }} item
     * @returns {string}
     */
    metricStatusIcon(item) {
      if (item && item.empty) return '!'
      if (item && item.warn) return '!'
      return '✓'
    },
    /**
     * 打开报告后标记已读（详情不会自动已读，须显式调用）
     * @param {Object} data
     * @returns {Promise<void>}
     */
    async markReportReadIfNeeded(data) {
      const reportId = data && data.id != null ? String(data.id).trim() : ''
      if (!reportId || data.isRead === true) return
      try {
        const res = await markHealthReportV2AsRead(reportId)
        if (res && (res.code === '1000' || res.code === 1000)) {
          data.isRead = true
          this.applyLocalDailyRead()
          // 数量可能与乐观扣减不一致时再拉一次
          this.fetchReportUnread()
        }
      } catch (e) {
        console.error('标记日报已读失败:', e)
      }
    },
    /**
     * 拉取日报并渲染
     * @returns {Promise<void>}
     */
    async fetchReport() {
      this.loading = true
      this.loadError = ''
      this.disposeCharts()
      try {
        const reportId = this.$route.query.reportId
          ? String(this.$route.query.reportId).trim()
          : ''
        let res
        if (reportId) {
          res = await getHealthReportDailyV2Detail(reportId)
        } else {
          const reportDate = resolveDailyReportDate(this.$route)
          res = await getHealthReportDailyV2({ reportDate: reportDate })
        }
        if (!isReportOk(res)) {
          throw new Error((res && res.msg) || '加载健康日报失败')
        }
        this.applyDaily(res.data)
        this.markReportReadIfNeeded(res.data)
        this.syncActiveModuleFromQuery()
        this.ensureActiveModuleVisible()
        this.loading = false
        await this.$nextTick()
        this.scrollActiveModuleTabIntoView()
        this.initAllCharts()
      } catch (e) {
        this.loadError = (e && e.message) || '加载健康日报失败'
        this.loading = false
      }
    },
    /**
     * 模块 Tab 是否有可展示数据
     * @param {string} key
     * @returns {boolean}
     */
    isModuleTabVisible(key) {
      if (key === 'overview') return (this.basicMetrics || []).length > 0
      if (key === 'warn') return (this.warnTypes || []).length > 0
      if (key === 'tcm') return !!this.hasHeartAssessData
      if (key === 'hr') return !!this.hasHrData
      if (key === 'bp') return !!(this.hasWatchBp || this.hasMeterBp)
      if (key === 'sleep') return !!this.hasSleepData
      return true
    },
    /**
     * 当前 Tab 被隐藏时，落到第一个有数据的模块
     * @returns {void}
     */
    ensureActiveModuleVisible() {
      const tabs = this.visibleModuleTabs || []
      if (!tabs.length) return
      if (tabs.some((item) => item.key === this.activeModule)) return
      this.activeModule = tabs[0].key
    },
    /**
     * 根据路由 query.tab 同步模块 Tab
     * @param {string} [tab]
     * @returns {void}
     */
    syncActiveModuleFromQuery(tab) {
      const raw = tab != null ? tab : (this.$route.query && this.$route.query.tab)
      const key = String(raw || '').trim()
      if (!key) return
      if (!this.isModuleTabVisible(key)) return
      const matched = (this.moduleTabs || []).some((item) => item.key === key)
      if (matched) this.activeModule = key
    },
    /**
     * 更新模块 Tab 左右滑动提示状态
     * @returns {void}
     */
    updateTabsScrollHint() {
      const el = this.$refs.moduleTabs
      if (!el) {
        this.tabsCanScrollLeft = false
        this.tabsCanScrollRight = false
        return
      }
      const maxScroll = el.scrollWidth - el.clientWidth
      if (maxScroll <= 2) {
        this.tabsCanScrollLeft = false
        this.tabsCanScrollRight = false
        return
      }
      this.tabsCanScrollLeft = el.scrollLeft > 2
      this.tabsCanScrollRight = el.scrollLeft < maxScroll - 2
    },
    /**
     * 将当前激活的模块 Tab 滚入可视区，并刷新滑动提示
     * @returns {void}
     */
    scrollActiveModuleTabIntoView() {
      const nav = this.$refs.moduleTabs
      if (!nav) return
      const active = nav.querySelector('.hds-module-tab.active')
      if (active && typeof active.scrollIntoView === 'function') {
        active.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'auto' })
      }
      this.updateTabsScrollHint()
    },
    /**
     * 返回上一页
     * @returns {void}
     */
    goBack() {
      this.$router.go(-1)
    },
    /**
     * 进入历史日报列表
     * @returns {void}
     */
    goHistoryList() {
      this.$router.push({ name: 'healthDailyShowcaseHistory' }).catch(() => {})
    },
    /**
     * 将 V2 日报 data 映射到页面
     * @param {Object} data
     * @returns {void}
     */
    applyDaily(data) {
      this.reportDate = data.reportDate || ''
      this.hasMonitoringData = !!data.hasMonitoringData
      this.healthScore = this.hasMonitoringData && data.healthScore != null ? data.healthScore : null
      this.headerSummary = Array.isArray(data.headerSummary)
        ? data.headerSummary.map((item) => String(item || '').trim()).filter(Boolean)
        : []
      this.dayStatus = {
        label: data.overallStatus || (this.hasMonitoringData ? '' : '暂无监测'),
        color: statusColor(data.overallStatus)
      }
      this.disclaimer = data.disclaimer || ''
      this.basicMetrics = (data.overview || []).map((m) => metricRow(m))

      const warn = data.warn || {}
      this.warnTypes = mapWarnTypes(warn.typeCounts)
      this.warnTimeline = (warn.points || []).map((p) => ({
        hour: parseHour(p.occurTime),
        color: warnHex(p.color),
        name: p.warnTypeName,
        content: p.content
      }))
      this.warnEmptyCopy = warn.emptyCopy || (this.warnTypes.length ? '' : '今日暂无健康预警')

      const tcm = data.tcm || {}
      this.hasHeartAssessData = !!tcm.evaluable
      this.heartAssessTip = tcm.insufficientCopy || '佩戴时间不足，无法评估心脏健康状态'
      const mapped = mapTcm(tcm)
      this.organTabs = mapped.tabs
      this.tcmOrgans = mapped.organs
      this.activeOrgan = mapped.active

      const heart = data.heart || {}
      this.hrSeries = heart.details || []
      this.hrTimes = heart.detailTimes || []
      this.hrMaxMetric = {
        value: heart.max && heart.max.value != null ? Number(heart.max.value) : null,
        warn: metricWarn(heart.max)
      }
      this.hrMinMetric = {
        value: heart.min && heart.min.value != null ? Number(heart.min.value) : null,
        warn: metricWarn(heart.min)
      }
      this.hrMetrics = [
        metricRow(heart.avg, '平均心率'),
        metricRow(heart.max, '最高值'),
        metricRow(heart.min, '最低心率'),
        metricRow(heart.resting, '静息心率')
      ]
      this.hrIdealText = heart.idealText || ''
      this.hrAdvice = data.heartAiAdvice ? splitAdvice(data.heartAiAdvice) : []
      this.hrWarnTips = heart.healthTips || []

      const wbp = data.watchBloodPressure || {}
      this.watchBpEmptyCopy = wbp.emptyCopy || '今日暂无手表血压数据'
      this.hasWatchBp = !wbp.emptyCopy && (
        hasRealMetric(wbp.avgSystolic) ||
        hasRealMetric(wbp.avgDiastolic) ||
        hasNumericPoints(wbp.systolicDetails)
      )
      this.watchSys = wbp.systolicDetails || []
      this.watchDia = wbp.diastolicDetails || []
      this.watchTimes = wbp.detailTimes || []
      this.watchBpMaxSysMetric = {
        value: wbp.maxSystolic && wbp.maxSystolic.value != null ? Number(wbp.maxSystolic.value) : null,
        warn: metricWarn(wbp.maxSystolic)
      }
      this.watchBpMaxDiaMetric = {
        value: wbp.maxDiastolic && wbp.maxDiastolic.value != null ? Number(wbp.maxDiastolic.value) : null,
        warn: metricWarn(wbp.maxDiastolic)
      }
      this.watchBpMetrics = [
        metricRow(wbp.avgSystolic, '平均收缩压'),
        metricRow(wbp.avgDiastolic, '平均舒张压'),
        metricRow(wbp.maxSystolic, '最高收缩压'),
        metricRow(wbp.maxDiastolic, '最高舒张压')
      ]

      const mbp = data.monitorBloodPressure || {}
      this.hasMeterBp = !!mbp.measured
      this.meterEmptyCopy = mbp.emptyCopy || '今日未测血压。建议养成定期测量习惯，捕捉血压波动是预防疾病的关键。'
      this.meterRecords = (mbp.records || []).map((r) => ({
        time: formatHm(r.measureTime),
        systolic: r.systolic != null ? r.systolic + 'mmHg' : '—',
        diastolic: r.diastolic != null ? r.diastolic + 'mmHg' : '—',
        warn: r.status === 'ABNORMAL',
        empty: r.status === 'NO_DATA'
      }))
      this.idealBp = parseIdealBp(mbp.idealSystolic != null ? mbp : wbp)
      this.bpAdvice = data.bloodPressureAiAdvice ? splitAdvice(data.bloodPressureAiAdvice) : []
      this.bpWarnTips = mbp.healthTips || []

      const sleep = data.sleep || {}
      this.hasSleepData = !!sleep.hasData
      const sleepTip = '睡眠时间尚未佩戴，暂无数据'
      const sleepCopy = String(sleep.emptyCopy || '').trim()
      this.sleepEmptyDesc = sleepCopy && sleepCopy !== sleepTip ? sleepCopy : ''
      const abn = sleep.abnormalItems || []
      const warnText = abn.length ? abn[0] : ''
      this.sleepData = {
        totalText: sleep.totalTime || '',
        timeRange: '',
        score: sleep.score != null ? sleep.score : 0,
        warnText: warnText,
        warnOk: !warnText || warnText === '睡眠各指标正常',
        suggestion: abn.filter((t) => t && t !== '睡眠各指标正常').join('；'),
        stages: [
          { label: '深睡时长', value: (sleep.deepSleepMinutes || 0) + ' 分钟', color: '#7c4dff', minutes: sleep.deepSleepMinutes || 0 },
          { label: '浅睡时长', value: (sleep.lightSleepMinutes || 0) + ' 分钟', color: '#b39ddb', minutes: sleep.lightSleepMinutes || 0 },
          { label: '快速眼动', value: (sleep.remMinutes || 0) + ' 分钟', color: '#00bb82', minutes: sleep.remMinutes || 0 }
        ],
        aiAdvice: data.sleepAiAdvice ? splitAdvice(data.sleepAiAdvice) : []
      }
    },
    /**
     * 是否为提示类摘要（含警告标识）
     * @param {string} text
     * @returns {boolean}
     */
    isHeaderSummaryWarning(text) {
      const value = String(text || '').trim()
      return value.indexOf('⚠') >= 0 || value.indexOf('提示') >= 0
    },
    /**
     * @returns {void}
     */
    disposeCharts() {
      Object.keys(this.chartMap).forEach((key) => {
        const chart = this.chartMap[key]
        if (chart) {
          try {
            chart.dispose()
          } catch (e) {
            /* ignore disposed dom */
          }
        }
      })
      this.chartMap = {}
    },
    /**
     * 切换模块 Tab
     * @param {string} key
     * @returns {void}
     */
    switchModule(key) {
      if (!key || this.activeModule === key) return
      this.disposeCharts()
      this.activeModule = key
      this.$nextTick(() => {
        if (this.activeModule !== key) return
        this.scrollActiveModuleTabIntoView()
        this.initChartsForModule(key)
      })
    },
    /**
     * 按当前模块初始化图表（v-if 切换后需重建）
     * @param {string} [moduleKey]
     * @returns {void}
     */
    initChartsForModule(moduleKey) {
      const key = moduleKey || this.activeModule
      if (key === 'warn' && this.warnTimeline.length) this.initWarnTimeline()
      if (key === 'hr') this.initHrChart()
      if (key === 'bp' && this.hasWatchBp) this.initWatchBpChart()
      if (key === 'sleep' && this.hasSleepData) this.initSleepScoreChart()
    },
    /**
     * @returns {void}
     */
    initAllCharts() {
      this.initChartsForModule(this.activeModule)
    },
    /**
     * @param {string} key
     * @param {HTMLElement} el
     * @returns {echarts.ECharts|null}
     */
    ensureChart(key, el) {
      if (!el) return null
      const existing = this.chartMap[key]
      if (existing) {
        if (existing.getDom && existing.getDom() === el) return existing
        try {
          existing.dispose()
        } catch (e) {
          /* ignore */
        }
        delete this.chartMap[key]
      }
      const mounted = echarts.getInstanceByDom(el)
      if (mounted) {
        try {
          mounted.dispose()
        } catch (e) {
          /* ignore */
        }
      }
      this.chartMap[key] = echarts.init(el)
      return this.chartMap[key]
    },
    /**
     * 健康预警时间散点
     * @returns {void}
     */
    initWarnTimeline() {
      const chart = this.ensureChart('warnTimelineChart', this.$refs.warnTimelineChart)
      if (!chart) return
      const points = this.warnTimeline
      const data = points.map((item, idx) => ({
        value: [item.hour, 1 + (idx % 2) * 0.35],
        itemStyle: { color: item.color || '#ff9800' },
        name: item.name,
        content: item.content
      }))
      chart.setOption({
        grid: { left: 16, right: 16, top: 24, bottom: 36 },
        tooltip: {
          trigger: 'item',
          confine: true,
          extraCssText: 'max-width: 140px; white-space: normal; word-break: break-all; line-height: 1.4;',
          formatter: (p) => {
            const d = p && p.data
            if (!d) return ''
            const h = Number(d.value && d.value[0])
            const hh = Math.floor(h)
            const mm = Math.round((h - hh) * 60)
            const time = String(hh).padStart(2, '0') + ':' + String(mm).padStart(2, '0')
            return (d.name || '预警') + ' ' + time + (d.content ? '<br/>' + d.content : '')
          }
        },
        xAxis: {
          type: 'value',
          min: 6,
          max: 22,
          interval: 1,
          axisTick: { show: false },
          axisLine: { lineStyle: { color: '#d9e2de' } },
          splitLine: { show: false },
          axisLabel: {
            color: '#8a9691',
            fontSize: 12,
            formatter: (v) => {
              const marks = [6, 9, 12, 14, 19, 22]
              return marks.indexOf(v) >= 0 ? v + ':00' : ''
            }
          }
        },
        yAxis: {
          type: 'value',
          min: 0,
          max: 2.2,
          show: false
        },
        series: [
          {
            type: 'scatter',
            symbolSize: 12,
            data: data
          }
        ]
      }, true)
    },
    /**
     * @param {string[]} times
     * @param {Array} values
     * @returns {string[]}
     */
    buildTimeAxis(times, values) {
      if (times && times.length && values && times.length === values.length) {
        return times.map((t) => formatHm(t) || t)
      }
      const hours = []
      for (let i = 0; i < 24; i += 1) {
        hours.push(String(i).padStart(2, '0') + ':00')
      }
      return hours
    },
    /**
     * 在趋势序列中定位与目标值最接近的下标
     * @param {(number|null)[]} series
     * @param {number} target
     * @returns {number}
     */
    findSeriesIndex(series, target) {
      let bestIdx = -1
      let minDiff = Infinity
      ;(series || []).forEach((value, idx) => {
        if (value == null || value === '') return
        const num = Number(value)
        if (Number.isNaN(num)) return
        const diff = Math.abs(num - target)
        if (diff < minDiff) {
          minDiff = diff
          bestIdx = idx
        }
      })
      return bestIdx
    },
    /**
     * 异常数据 markPoint 样式
     * @param {Array} points
     * @returns {Object|undefined}
     */
    buildAbnormalMarkPointOption(points) {
      if (!points || !points.length) return undefined
      return {
        symbol: 'circle',
        symbolSize: 9,
        data: points,
        itemStyle: {
          color: '#ff9800',
          borderColor: '#fff',
          borderWidth: 2,
          shadowColor: 'rgba(255, 152, 0, 0.25)',
          shadowBlur: 6
        },
        label: { show: false }
      }
    },
    /**
     * 构建单条序列的异常 markPoint
     * @param {string[]} axis
     * @param {(number|null)[]} series
     * @param {{ value: number|null, warn: boolean }} metric
     * @param {string} name
     * @returns {Array<{ name: string, coord: (string|number)[], value: number }>}
     */
    buildMetricMarkPoints(axis, series, metric, name) {
      if (!metric || !metric.warn || metric.value == null) return []
      const idx = this.findSeriesIndex(series, metric.value)
      if (idx < 0) return []
      const num = Number(series[idx])
      return [{
        name: name,
        coord: [axis[idx], num],
        value: num
      }]
    },
    /**
     * 构建心率异常 markPoint（仅接口判定异常的最高/最低值）
     * @param {string[]} axis
     * @param {(number|null)[]} series
     * @returns {Array<{ name: string, coord: (string|number)[], value: number }>}
     */
    buildHrAbnormalMarkPoints(axis, series) {
      const points = []
      const usedIdx = new Set()
      const targets = [
        { metric: this.hrMaxMetric, name: '偏高' },
        { metric: this.hrMinMetric, name: '偏低' }
      ]
      targets.forEach(({ metric, name }) => {
        if (!metric || !metric.warn || metric.value == null) return
        const idx = this.findSeriesIndex(series, metric.value)
        if (idx < 0 || usedIdx.has(idx)) return
        usedIdx.add(idx)
        const num = Number(series[idx])
        points.push({
          name: name,
          coord: [axis[idx], num],
          value: num
        })
      })
      return points
    },
    /**
     * 心率全天折线
     * @returns {void}
     */
    initHrChart() {
      const chart = this.ensureChart('hrChart', this.$refs.hrChart)
      if (!chart) return
      const axis = this.buildTimeAxis(this.hrTimes, this.hrSeries)
      const showIdx = [0, Math.floor((axis.length - 1) / 4), Math.floor((axis.length - 1) / 2), Math.floor(((axis.length - 1) * 3) / 4), axis.length - 1]
      const hrLineColor = '#ffb3ad'
      const abnormalPoints = this.buildHrAbnormalMarkPoints(axis, this.hrSeries)
      chart.setOption({
        grid: { left: 12, right: 36, top: 20, bottom: 28 },
        tooltip: { trigger: 'axis' },
        xAxis: {
          type: 'category',
          data: axis,
          boundaryGap: false,
          axisTick: { show: false },
          axisLine: { lineStyle: { color: '#d9e2de' } },
          axisLabel: {
            color: '#8a9691',
            fontSize: 12,
            interval: 0,
            formatter: (v, idx) => (showIdx.indexOf(idx) >= 0 ? v : '')
          }
        },
        yAxis: {
          type: 'value',
          min: 20,
          max: 220,
          interval: 50,
          position: 'right',
          axisTick: { show: false },
          axisLine: { show: false },
          splitLine: { lineStyle: { color: '#eef3f1', type: 'dashed' } },
          axisLabel: { color: '#8a9691', fontSize: 12 }
        },
        series: [
          {
            type: 'line',
            data: this.hrSeries,
            smooth: true,
            connectNulls: false,
            showSymbol: false,
            symbol: 'circle',
            symbolSize: 8,
            lineStyle: { width: 2, color: hrLineColor },
            itemStyle: { color: hrLineColor },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(255, 179, 173, 0.12)' },
                { offset: 1, color: 'rgba(255, 179, 173, 0.02)' }
              ])
            },
            markPoint: this.buildAbnormalMarkPointOption(abnormalPoints)
          }
        ]
      }, true)
    },
    /**
     * 手表血压双线（收缩压蓝 / 舒张压绿）
     * @returns {void}
     */
    initWatchBpChart() {
      const chart = this.ensureChart('watchBpChart', this.$refs.watchBpChart)
      if (!chart) return
      const axis = this.buildTimeAxis(this.watchTimes, this.watchSys.length ? this.watchSys : this.watchDia)
      const showIdx = [0, Math.floor((axis.length - 1) / 4), Math.floor((axis.length - 1) / 2), Math.floor(((axis.length - 1) * 3) / 4), axis.length - 1]
      const nums = this.watchSys.concat(this.watchDia).filter((v) => v != null).map(Number)
      const minY = nums.length ? Math.max(40, Math.floor((Math.min.apply(null, nums) - 10) / 10) * 10) : 60
      const maxY = nums.length ? Math.min(200, Math.ceil((Math.max.apply(null, nums) + 10) / 10) * 10) : 160
      const sysLineColor = '#90caf9'
      const diaLineColor = '#7ad9b5'
      const sysAbnormalPoints = this.buildMetricMarkPoints(
        axis,
        this.watchSys,
        this.watchBpMaxSysMetric,
        '收缩压异常'
      )
      const diaAbnormalPoints = this.buildMetricMarkPoints(
        axis,
        this.watchDia,
        this.watchBpMaxDiaMetric,
        '舒张压异常'
      )
      chart.setOption({
        grid: { left: 12, right: 36, top: 20, bottom: 28 },
        tooltip: { trigger: 'axis' },
        xAxis: {
          type: 'category',
          data: axis,
          boundaryGap: false,
          axisTick: { show: false },
          axisLine: { lineStyle: { color: '#d9e2de' } },
          axisLabel: {
            color: '#8a9691',
            fontSize: 12,
            interval: 0,
            formatter: (v, idx) => (showIdx.indexOf(idx) >= 0 ? v : '')
          }
        },
        yAxis: {
          type: 'value',
          min: minY,
          max: maxY,
          position: 'right',
          axisTick: { show: false },
          axisLine: { show: false },
          splitLine: { lineStyle: { color: '#eef3f1', type: 'dashed' } },
          axisLabel: { color: '#8a9691', fontSize: 12 }
        },
        series: [
          {
            name: '收缩压',
            type: 'line',
            data: this.watchSys,
            smooth: true,
            connectNulls: false,
            showSymbol: false,
            lineStyle: { width: 2, color: sysLineColor },
            itemStyle: { color: sysLineColor },
            markPoint: this.buildAbnormalMarkPointOption(sysAbnormalPoints)
          },
          {
            name: '舒张压',
            type: 'line',
            data: this.watchDia,
            smooth: true,
            connectNulls: false,
            showSymbol: false,
            lineStyle: { width: 2, color: diaLineColor },
            itemStyle: { color: diaLineColor },
            markPoint: this.buildAbnormalMarkPointOption(diaAbnormalPoints)
          }
        ]
      }, true)
    },
    /**
     * 睡眠得分环形图
     * @returns {void}
     */
    initSleepScoreChart() {
      if (!this.hasSleepData) return
      const chart = this.ensureChart('sleepScoreChart', this.$refs.sleepScoreChart)
      if (!chart) return
      const stages = this.sleepData.stages
      chart.setOption({
        series: [
          {
            type: 'pie',
            radius: ['62%', '78%'],
            center: ['50%', '50%'],
            silent: true,
            label: { show: false },
            data: stages.map((s) => ({
              value: s.minutes,
              name: s.label,
              itemStyle: { color: s.color }
            }))
          },
          {
            type: 'pie',
            radius: ['78%', '84%'],
            center: ['50%', '50%'],
            silent: true,
            label: { show: false },
            data: [
              {
                value: 1,
                itemStyle: {
                  color: 'transparent',
                  borderColor: '#e5ece9',
                  borderWidth: 1,
                  borderType: 'dashed'
                }
              }
            ]
          }
        ],
        graphic: [
          {
            type: 'group',
            left: 'center',
            top: 'middle',
            children: [
              {
                type: 'text',
                style: {
                  text: String(this.sleepData.score),
                  fill: '#12231c',
                  fontSize: 33,
                  fontWeight: 700,
                  textAlign: 'center',
                  textVerticalAlign: 'middle',
                  y: -8
                }
              },
              {
                type: 'text',
                style: {
                  text: '睡眠得分',
                  fill: '#8a9691',
                  fontSize: 13,
                  textAlign: 'center',
                  textVerticalAlign: 'middle',
                  y: 16
                }
              }
            ]
          }
        ]
      })
    },
    handleResize() {
      this.updateTabsScrollHint()
      Object.keys(this.chartMap).forEach((key) => {
        const chart = this.chartMap[key]
        if (!chart || !chart.getDom) return
        const dom = chart.getDom()
        if (dom && dom.isConnected) {
          chart.resize()
        }
      })
    },
    /**
     * 跳转现网中医报告
     * @returns {void}
     */
    onTcmDetail() {
      this.$router.push({ name: 'chineseMedicineIndex' })
    }
  }
}
</script>

<style scoped>
.hds-page {
  position: relative;
  min-height: 100vh;
  background: #f5f7f8;
  overflow-x: hidden;
}
.hds-bg {
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
}
.hds-bg-gradient {
  width: 100%;
  height: 100%;
  background: linear-gradient(180deg, #a0edd2 0%, #eefaf5 22%, #f5f7f8 48%, #f5f7f8 100%);
}
.hds-main {
  position: relative;
  z-index: 1;
  max-width: 480px;
  margin: 0 auto;
  padding: 0 16px 40px;
  box-sizing: border-box;
}
.hds-nav {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 44px;
  margin-bottom: 8px;
}
.hds-nav-back {
  position: relative;
  z-index: 1;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  padding: 0;
  background: transparent;
  color: #12231c;
  font-size: 22px;
}
.hds-nav-title {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  margin: 0;
  max-width: calc(100% - 160px);
  text-align: center;
  font-size: 18px;
  font-weight: 700;
  color: #000;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: none;
}
.hds-history-btn {
  position: relative;
  z-index: 1;
  flex-shrink: 0;
  height: 28px;
  padding: 0 12px;
  border: none;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.78);
  color: #0f7a56;
  font-size: 12px;
  font-weight: 600;
  line-height: 28px;
}
.hds-history-btn:active {
  opacity: 0.85;
}
.hds-content-date {
  margin-bottom: 10px;
  font-size: 14px;
  font-weight: 600;
  color: #5f726a;
}
.hds-loading {
  text-align: center;
  padding: 80px 16px;
  color: #5f726a;
  font-size: 15px;
}
.hds-retry {
  margin-top: 16px;
  width: 140px;
  height: 36px;
  border: none;
  border-radius: 10px;
  background: #00bb82;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
}
.hds-disclaimer {
  margin: 16px 0 0;
  font-size: 12px;
  line-height: 1.5;
  color: #9aa3a0;
}
.hds-hero { margin-bottom: 12px; }
.hds-module-tabs-wrap {
  position: relative;
  margin: 0 -4px 14px;
}
.hds-module-tabs-wrap::before,
.hds-module-tabs-wrap::after {
  content: '';
  position: absolute;
  top: 0;
  bottom: 6px;
  width: 48px;
  z-index: 1;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.2s ease;
}
.hds-module-tabs-wrap::before {
  left: 0;
  background: linear-gradient(to right, #f5f7f8 35%, rgba(245, 247, 248, 0));
}
.hds-module-tabs-wrap::after {
  right: 0;
  background: linear-gradient(to left, #f5f7f8 35%, rgba(245, 247, 248, 0));
}
.hds-module-tabs-wrap.is-scroll-left::before,
.hds-module-tabs-wrap.is-scroll-right::after {
  opacity: 1;
}
.hds-tabs-hint {
  position: absolute;
  top: 50%;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin-top: -15px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(0, 187, 130, 0.18);
  color: #00bb82;
  box-shadow: 0 2px 10px rgba(18, 35, 28, 0.08);
  pointer-events: none;
  backdrop-filter: blur(4px);
}
.hds-tabs-hint--left {
  left: 2px;
  animation: hds-tabs-hint-pulse 1.6s ease-in-out infinite;
}
.hds-tabs-hint--right {
  right: 2px;
  animation: hds-tabs-hint-pulse 1.6s ease-in-out infinite;
}
@keyframes hds-tabs-hint-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.72; transform: scale(0.96); }
}
.hds-module-tabs {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  padding: 2px 4px 8px;
  scrollbar-width: none;
}
.hds-module-tabs::-webkit-scrollbar {
  display: none;
}
.hds-module-tab {
  flex: 0 0 auto;
  height: 32px;
  padding: 0 14px;
  border: none;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.72);
  color: #5f726a;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  line-height: 32px;
}
.hds-module-tab.active {
  background: linear-gradient(160deg, #2fd5a0 0%, #00bb82 55%, #00a572 100%);
  color: #fff;
}
.hds-module-body {
  min-height: 200px;
}
.hds-content-date + .hds-day-status {
  margin-top: 10px;
}
.hds-day-status {
  margin-top: 0;
  overflow: hidden;
  background: linear-gradient(180deg, #ffffff 0%, #f8fcfa 100%);
  border: 1px solid rgba(0, 194, 117, 0.12);
  border-radius: 16px;
  box-shadow: 0 6px 18px rgba(18, 35, 28, 0.05);
}
.hds-day-status-head {
  padding: 18px 16px 16px;
  text-align: center;
  background: linear-gradient(180deg, rgba(0, 194, 117, 0.07) 0%, rgba(255, 255, 255, 0) 100%);
}
.hds-day-status-head-label {
  font-size: 13px;
  font-weight: 600;
  color: #6b7a74;
  letter-spacing: 0.04em;
}
.hds-day-status-score-row {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 12px;
}
.hds-day-status-score {
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  line-height: 1;
}
.hds-day-status-num {
  font-size: 41px;
  font-weight: 800;
  color: #12231c;
  letter-spacing: -0.04em;
}
.hds-day-status-unit {
  font-size: 15px;
  font-weight: 600;
  color: #8a9691;
  transform: translateY(-2px);
}
.hds-day-status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 64px;
  min-height: 26px;
  padding: 0 14px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: 0.06em;
  white-space: nowrap;
}
.hds-day-status-badge.is-excellent {
  color: #00bb82;
  background: rgba(0, 187, 130, 0.12);
  box-shadow: inset 0 0 0 1px rgba(0, 187, 130, 0.22);
}
.hds-day-status-badge.is-good {
  color: #ff9800;
  background: rgba(255, 152, 0, 0.12);
  box-shadow: inset 0 0 0 1px rgba(255, 152, 0, 0.22);
}
.hds-day-status-badge.is-fair {
  color: #ff8a80;
  background: rgba(255, 138, 128, 0.12);
  box-shadow: inset 0 0 0 1px rgba(255, 138, 128, 0.22);
}
.hds-day-status-badge.is-default {
  color: #5f726a;
  background: rgba(95, 114, 106, 0.1);
  box-shadow: inset 0 0 0 1px rgba(95, 114, 106, 0.12);
}
.hds-day-status-summaries {
  list-style: none;
  margin: 0;
  padding: 4px 14px 14px;
}
.hds-day-status-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 0;
}
.hds-day-status-item + .hds-day-status-item {
  border-top: 1px solid rgba(0, 0, 0, 0.05);
}
.hds-day-status-dot {
  flex-shrink: 0;
  width: 6px;
  height: 6px;
  margin-top: 8px;
  border-radius: 50%;
  background: #00c275;
}
.hds-day-status-item.is-warning .hds-day-status-dot {
  background: #ff9800;
}
.hds-day-status-text {
  flex: 1;
  min-width: 0;
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.7;
  color: #3d4a45;
  text-align: left;
}
.hds-day-status-item.is-warning .hds-day-status-text {
  color: #c25d00;
  font-weight: 600;
}
.hds-day-status-head + .hds-day-status-summaries {
  border-top: 1px solid rgba(0, 194, 117, 0.1);
  padding-top: 6px;
}
.hds-card {
  background: #fff;
  border-radius: 14px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 6px 18px rgba(18, 35, 28, 0.04);
}
.hds-card--clip {
  position: relative;
}
.hds-card-head { margin-bottom: 8px; }
.hds-card-head--warn { margin-bottom: 6px; }
.hds-card-head--row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.hds-card-title {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: #12231c;
}
.hds-card-head--row .hds-card-title {
  text-align: left;
  width: auto;
}
.hds-sub-title {
  margin: 4px 0 10px;
  font-size: 15px;
  font-weight: 700;
  color: #1a2a24;
}
.hds-subtitle {
  margin: 0 0 10px;
  font-size: 13px;
  color: #8a9691;
  text-align: center;
  line-height: 1.5;
}
.hds-cat-title {
  margin: 4px 0 8px;
  font-size: 15px;
  font-weight: 700;
  color: #ff9800;
}
.hds-muted { font-size: 13px; color: #8a9691; }
.hds-hint {
  margin: 10px 0;
  text-align: center;
  font-size: 13px;
  color: #8a9691;
}
.hds-empty-tip {
  margin: 18px 0;
  text-align: center;
  font-size: 15px;
  color: #667570;
}
.hds-organ-tabs {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-bottom: 10px;
}
.hds-organ-tab {
  border: 1px solid #e3ebe7;
  background: #fff;
  color: #334640;
  border-radius: 999px;
  height: 34px;
  font-size: 14px;
  cursor: pointer;
}
.hds-organ-tab.active {
  background: linear-gradient(180deg, #1ad08f 0%, #00bb82 100%);
  border-color: transparent;
  color: #fff;
  font-weight: 600;
}
.hds-dim-tabs {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px;
  margin: 8px 0 10px;
}
.hds-dim-tab {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  min-height: 36px;
  padding: 6px 10px;
  border-radius: 8px;
  background: rgba(138, 150, 145, 0.08);
  border: 1px solid rgba(138, 150, 145, 0.14);
  box-sizing: border-box;
}
.hds-dim-tab.is-excellent {
  background: linear-gradient(160deg, #2fd5a0 0%, #00bb82 55%, #00a572 100%);
  border-color: transparent;
}
.hds-dim-tab.is-good {
  background: linear-gradient(160deg, #ffb347 0%, #ff9800 55%, #f08800 100%);
  border-color: transparent;
}
.hds-dim-tab.is-fair {
  background: linear-gradient(160deg, #ffb0a8 0%, #ff8a80 55%, #f07066 100%);
  border-color: transparent;
}
.hds-dim-tab-label {
  flex: 1;
  min-width: 0;
  font-size: 12px;
  font-weight: 600;
  color: #6b7a74;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.hds-dim-tab-score {
  flex-shrink: 0;
  display: inline-flex;
  align-items: baseline;
  font-size: 17px;
  font-weight: 700;
  color: #8a9691;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}
.hds-dim-tab.is-excellent .hds-dim-tab-label,
.hds-dim-tab.is-excellent .hds-dim-tab-score,
.hds-dim-tab.is-excellent .hds-dim-tab-unit,
.hds-dim-tab.is-good .hds-dim-tab-label,
.hds-dim-tab.is-good .hds-dim-tab-score,
.hds-dim-tab.is-good .hds-dim-tab-unit,
.hds-dim-tab.is-fair .hds-dim-tab-label,
.hds-dim-tab.is-fair .hds-dim-tab-score,
.hds-dim-tab.is-fair .hds-dim-tab-unit {
  color: #fff;
}
.hds-dim-tab-unit {
  margin-left: 1px;
  font-size: 11px;
  font-weight: 600;
  color: #8a9691;
}
.hds-dim-info {
  background: #f7faf9;
  border-radius: 10px;
  padding: 10px 12px;
  margin-bottom: 12px;
}
.hds-dim-row {
  display: flex;
  gap: 10px;
  padding: 6px 0;
  font-size: 14px;
}
.hds-dim-label {
  color: #00a865;
  font-weight: 600;
  flex-shrink: 0;
}
.hds-dim-value { color: #4a5b54; }
.hds-heart-assess {
  padding-top: 2px;
}
.hds-heart-level {
  text-align: center;
  margin: 4px 0 8px;
}
.hds-heart-level-text {
  font-size: 29px;
  font-weight: 700;
  color: #12231c;
  line-height: 1.2;
}
.hds-tcm-btn {
  display: block;
  width: 100%;
  height: 42px;
  border: none;
  border-radius: 10px;
  background: #f0f3f2;
  color: #12231c;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
}
.hds-tcm-btn:active {
  opacity: 0.9;
}
.hds-desc {
  margin: 0;
  font-size: 14px;
  line-height: 1.7;
  color: #667570;
}
.hds-chart { width: 100%; }
.hds-chart--timeline { height: 120px; }
.hds-chart--line { height: 200px; }
.hds-chart--sleep { height: 180px; }

.hds-sleep-panel {
  background: linear-gradient(180deg, #f4fbf8 0%, #f7faf9 100%);
  border-radius: 12px;
  padding: 14px 12px 12px;
  margin-bottom: 12px;
}
.hds-sleep-total {
  text-align: center;
  font-size: 23px;
  font-weight: 700;
  color: #12231c;
}
.hds-sleep-range {
  margin-top: 4px;
  text-align: center;
  font-size: 13px;
  color: #8a9691;
}
.hds-sleep-stages {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-top: 4px;
}
.hds-sleep-stage {
  text-align: center;
}
.hds-sleep-stage-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-bottom: 4px;
}
.hds-sleep-stage-label {
  font-size: 13px;
  color: #667570;
}
.hds-sleep-stage-value {
  margin-top: 2px;
  font-size: 14px;
  font-weight: 600;
  color: #12231c;
}
.hds-sleep-warn-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
}
.hds-sleep-warn-text {
  font-size: 16px;
  font-weight: 700;
  color: #12231c;
}

.hds-metric-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.hds-metric-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 11px 0;
  border-top: 1px solid #eef2f0;
  font-size: 15px;
}
.hds-metric-row:first-child { border-top: none; }
.hds-metric-label { flex: 1; color: #4a5b54; }
.hds-metric-value { color: #12231c; font-weight: 600; }
.hds-metric-value.danger { color: #e53935; }
.hds-metric-value.muted { color: #9aa5a0; }
.hds-status {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
}
.hds-status.ok { background: #00bb82; }
.hds-status.warn { background: #ff9800; }
.hds-status.empty { background: #b8c0bc; }
.hds-arrow { color: #b0bbb6; font-size: 15px; }

.hds-warn-list {
  list-style: none;
  margin: 4px 0 0;
  padding: 0;
}
.hds-warn-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 4px;
  border-top: 1px solid #eef2f0;
}
.hds-warn-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.hds-warn-name { flex: 1; font-size: 15px; color: #1a2a24; }
.hds-warn-count {
  font-size: 15px;
  font-weight: 700;
  color: #e53935;
}

.hds-range-pill,
.hds-ideal-card {
  background: rgba(0, 187, 130, 0.1);
  border-radius: 10px;
  padding: 10px 12px;
  margin-top: 10px;
}
.hds-range-pill {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 14px;
  color: #3d5a50;
}
.hds-range-pill strong { color: #0f7a56; }
.hds-ideal-head {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  color: #1a2a24;
  font-weight: 600;
  margin-bottom: 8px;
}
.hds-ideal-body {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  color: #0f7a56;
  font-weight: 600;
}

.hds-ai-block { margin-top: 12px; }
.hds-advice {
  list-style: none;
  margin: 0;
  padding: 0;
}
.hds-advice li {
  position: relative;
  padding-left: 14px;
  margin-bottom: 10px;
  font-size: 14px;
  line-height: 1.7;
  color: #12231c;
}
.hds-advice li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 9px;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #12231c;
  opacity: 0.45;
}
.hds-advice li:last-child {
  margin: 12px 0 0;
  padding: 10px 12px 10px 28px;
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(0, 187, 130, 0.08) 0%, rgba(0, 187, 130, 0.03) 100%);
  border: 1px solid rgba(0, 187, 130, 0.14);
  color: #0f7a56;
  font-weight: 500;
}
.hds-advice li:last-child::before {
  left: 12px;
  top: 16px;
  width: 6px;
  height: 6px;
  background: #00bb82;
  opacity: 1;
  box-shadow: 0 0 0 3px rgba(0, 187, 130, 0.16);
}
.hds-advice li:only-child {
  margin-top: 0;
}
.hds-advice-num {
  list-style: none;
  margin: 0;
  padding: 0;
  color: #12231c;
  font-size: 14px;
  line-height: 1.7;
}
.hds-advice-num li {
  position: relative;
  padding-left: 14px;
  margin-bottom: 10px;
}
.hds-advice-num li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 9px;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #12231c;
  opacity: 0.45;
}
.hds-advice-num li::marker {
  content: none;
}
.hds-advice-num li:last-child {
  margin: 12px 0 0;
  padding: 10px 12px 10px 28px;
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(0, 187, 130, 0.08) 0%, rgba(0, 187, 130, 0.03) 100%);
  border: 1px solid rgba(0, 187, 130, 0.14);
  color: #0f7a56;
  font-weight: 500;
}
.hds-advice-num li:last-child::before {
  left: 12px;
  top: 16px;
  width: 6px;
  height: 6px;
  background: #00bb82;
  opacity: 1;
  box-shadow: 0 0 0 3px rgba(0, 187, 130, 0.16);
}
.hds-advice-num li:only-child {
  margin-top: 0;
}

.hds-bullet-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.hds-bullet-list li {
  position: relative;
  padding-left: 14px;
  margin-bottom: 10px;
  font-size: 14px;
  line-height: 1.7;
  color: #4a5b54;
}
.hds-bullet-list li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 8px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #1e88e5;
}
.hds-bullet-list >>> .c-danger {
  color: #e53935;
  font-weight: 700;
}

.hds-legend {
  display: flex;
  justify-content: center;
  gap: 16px;
  margin: 4px 0 8px;
  font-size: 13px;
  color: #5f726a;
}
.hds-legend-item { display: inline-flex; align-items: center; gap: 6px; }
.hds-legend-item .dot {
  width: 10px;
  height: 10px;
  border-radius: 3px;
  display: inline-block;
}
.hds-legend-item .dot.blue { background: #90caf9; }
.hds-legend-item .dot.green { background: #7ad9b5; }

.hds-bp-records {
  display: grid;
  gap: 10px;
}
.hds-bp-record {
  background: #f7faf9;
  border-radius: 10px;
  padding: 12px;
}
.hds-bp-record-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}
.hds-bp-time {
  font-size: 15px;
  font-weight: 600;
  color: #12231c;
}
.hds-bp-record-body {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  color: #4a5b54;
}
</style>



// WEBPACK FOOTER //
// src/views/healthDailyShowcase/index.vue